
import React, {
  useState,
  useCallback,
} from "react";

import { LoadingSpinner } from "../App";
import {
  SHEET_CONFIG,
  getSheetEditUrl,
} from "../constants";

import { useInventoryData } from "../hooks/useInventoryData";
import { useSheetTableFilters } from "../hooks/useSheetTableFilters";

import {
  getStatusHex,
  normalizeHeaderName,
} from "../utils/helpers";

import ImagePreviewCell from "../components/inventory/ImagePreviewCell";

import {
  FilterToolbar,
  DataTableCard,
  PaginationBar,
  PageError,
  SheetDataTable,
  Button,
  PAGE,
} from "../components/ui";

import HoverNav from "../components/ui/HoverNav";

import DocumentUploadPanel from "../components/inventory/DocumentUploadPanel";

import {
  getInventoryExcelRows,
  downloadInventoryExcel,
} from "../services/InventoryExcelApi";

const SHEET_URL = getSheetEditUrl(
  SHEET_CONFIG.MAIN_SHEET
);

const ROWS_PER_PAGE = 24;

const STATUS_PATTERN =
  /status|condition|scanned|psu|rail|power/i;

const IMAGE_PATTERN =
  /image|photo|picture|path|img/i;

const actionStyle = {
  height: 34,
  padding: "0 12px",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

export default function Inventory() {
  const {
    data,
    headers,
    loading,
    refreshing,
    error,
    refreshData,
  } = useInventoryData();

  const [view, setView] = useState("sheet");
  const [previousView, setPreviousView] =
    useState("sheet");

  const [backendData, setBackendData] =
    useState([]);

  const [backendHeaders, setBackendHeaders] =
    useState([]);

  const [backendFilters, setBackendFilters] =
    useState({});

  const [backendBusy, setBackendBusy] =
    useState(false);

  const [backendError, setBackendError] =
    useState("");

  const [backendLoaded, setBackendLoaded] =
    useState(false);

  // Load database Excel records.
  const loadBackend = useCallback(
    async (filters = {}) => {
      setBackendBusy(true);
      setBackendError("");

      try {
        const result =
          await getInventoryExcelRows(filters);

        setBackendData(result.data);
        setBackendHeaders(result.headers);
        setBackendFilters(filters);
        setBackendLoaded(true);

        setView("backend");

        return result;
      } catch (err) {
        console.error(
          "Backend inventory error:",
          err
        );

        setBackendError(
          `Failed to load inventory: ${err.message}`
        );

        // Keep current view so errors remain visible.
        throw err;
      } finally {
        setBackendBusy(false);
      }
    },
    []
  );

  function openUploadPanel() {
    setPreviousView(
      view === "backend" ? "backend" : "sheet"
    );
    setView("upload");
  }

  function closeUploadPanel() {
    setView(previousView);
  }

  async function showBackend() {
    if (backendLoaded) {
      setView("backend");
      return;
    }

    try {
      await loadBackend();
    } catch {
      // The error is shown in backendError.
    }
  }

  const activeData =
    view === "backend" ? backendData : data;

  const activeHeaders =
    view === "backend" ? backendHeaders : headers;

  const {
    searchTerm,
    setSearchTerm,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    page,
    setPage,
    allDates,
    filteredData,
    paginatedData,
    totalPages,
    clearFilters,
  } = useSheetTableFilters(
    activeData || [],
    { rowsPerPage: ROWS_PER_PAGE }
  );

  function resetPage() {
    setPage(1);
  }

  async function handleRefresh() {
    try {
      if (view === "backend") {
        await loadBackend(backendFilters);
      } else if (view === "sheet") {
        await refreshData();
      }
    } catch (err) {
      console.error("Refresh failed:", err);
    }
  }

  async function handleDownload() {
    setBackendError("");

    try {
      await downloadInventoryExcel(backendFilters);
    } catch (err) {
      setBackendError(
        `Download failed: ${err.message}`
      );
    }
  }

  async function handleViewInventory(filters) {
    await loadBackend(filters || {});
    resetPage();
    clearFilters();
  }

  function renderCell(row, header) {
    const value = row[header] ?? "";
    const headerName = String(header).toLowerCase();

    const isStatus =
      STATUS_PATTERN.test(headerName);

    const isImage =
      IMAGE_PATTERN.test(headerName);

    const displayValue =
      value !== null &&
      typeof value === "object"
        ? JSON.stringify(value)
        : String(value);

    return (
      <td
        style={{
          ...PAGE.type.tableCell,
          color: isStatus
            ? getStatusHex(displayValue)
            : PAGE.colors.text,
          fontWeight: isStatus ? 700 : 500,
          whiteSpace: isImage
            ? "normal"
            : "nowrap",
          padding: "8px 12px",
          fontSize: 12,
        }}
      >
        {isImage ? (
          <ImagePreviewCell value={displayValue} />
        ) : (
          displayValue || "—"
        )}
      </td>
    );
  }

  // Do not block uploads if the Sheet API fails.
  const showSheetLoading =
    view === "sheet" && loading;

  const showSheetError =
    view === "sheet" && error;

  return (
    <>
      <style>{`
        .inventory-page table {
          width: 100% !important;
          min-width: 1000px !important;
          border-collapse: separate !important;
          border-spacing: 0 !important;
          table-layout: auto !important;
        }

        .inventory-page thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 8 !important;
          background: #f8fafc !important;
          color: #334155 !important;
          padding: 9px 12px !important;
          font-size: 11px !important;
          white-space: nowrap !important;
          border-bottom:
            1px solid #dbe3ef !important;
        }

        .inventory-page tbody tr:nth-child(odd) td {
          background: #fff;
        }

        .inventory-page tbody tr:nth-child(even) td {
          background: #f9fbfd;
        }

        .inventory-page tbody tr:hover td {
          background: #eef6ff !important;
        }

        .inventory-page td {
          border-bottom:
            1px solid #edf2f7 !important;
          padding: 8px 12px !important;
          font-size: 12px !important;
        }

        .inventory-upload-btn {
          height: 34px;
          padding: 0 16px;
          border: none;
          border-radius: 8px;
          background: #2563eb;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          flex-shrink: 0;
          white-space: nowrap;
        }

        .inventory-upload-btn:hover {
          background: #1d4ed8;
        }

        .inventory-compact-toolbar input {
          font-size: 12px !important;
        }

        .inventory-compact-toolbar button {
          min-height: 34px !important;
          font-size: 11px !important;
        }
      `}</style>

      <div
        className="inventory-page"
        style={{
          height: "100%",
          background: PAGE.colors.bg,
          fontFamily: PAGE.fontFamily,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: PAGE.maxWidth,
            height: "100%",
            margin: "0 auto",
            padding: "12px 20px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Source selection */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 10,
              flexShrink: 0,
            }}
          >
            <Button
              variant={
                view === "sheet"
                  ? "success"
                  : "secondary"
              }
              style={actionStyle}
              onClick={() => {
                setView("sheet");
                resetPage();
              }}
            >
              Google Sheet
            </Button>

            <Button
              variant={
                view === "backend"
                  ? "success"
                  : "secondary"
              }
              style={actionStyle}
              onClick={showBackend}
            >
              AI Inventory
            </Button>

            <span
              style={{
                color: "#64748b",
                fontSize: 12,
              }}
            >
              {view === "backend"
                ? `${backendData.length} backend records`
                : view === "upload"
                  ? "Project document management"
                  : "Google Sheet inventory"}
            </span>
          </div>

          {/* Toolbar */}
          <div
            className="inventory-compact-toolbar"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <FilterToolbar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search inventory…"
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                dateMin={allDates?.[0]}
                dateMax={
                  allDates?.[allDates.length - 1]
                }
                dateLabel="Scan date"
                showClear={Boolean(
                  searchTerm || dateFrom || dateTo
                )}
                onClear={clearFilters}
                refreshing={
                  view === "backend"
                    ? backendBusy
                    : refreshing
                }
                onRefresh={handleRefresh}
                actions={
                  <>
                    {view === "backend" && (
                      <Button
                        variant="secondary"
                        style={actionStyle}
                        onClick={handleDownload}
                      >
                        Download Excel
                      </Button>
                    )}

                    <Button
                      variant="success"
                      style={actionStyle}
                      onClick={() =>
                        window.open(
                          SHEET_URL,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                    >
                      Open Google Sheet
                    </Button>
                  </>
                }
              />
            </div>

            {/* Always visible upload button */}
            <button
              type="button"
              className="inventory-upload-btn"
              onClick={
                view === "upload"
                  ? closeUploadPanel
                  : openUploadPanel
              }
            >
              {view === "upload"
                ? "← Back to Inventory"
                : "↑ Upload"}
            </button>
          </div>

          {/* API errors */}
          {backendError && (
            <div
              role="alert"
              style={{
                padding: 12,
                marginBottom: 10,
                borderRadius: 8,
                background: "#fef2f2",
                color: "#b91c1c",
                fontSize: 12,
                overflowWrap: "anywhere",
              }}
            >
              {backendError}
            </div>
          )}

          {/* Content */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {view === "upload" ? (
              <DocumentUploadPanel
                onViewInventory={
                  handleViewInventory
                }
              />
            ) : showSheetLoading ? (
              <LoadingSpinner
                title="Loading Inventory"
                subtitle="Fetching inventory records..."
              />
            ) : showSheetError ? (
              <div
                style={{
                  padding: 20,
                }}
              >
                <PageError message={error} />
                <button
                  onClick={showBackend}
                  style={{
                    ...actionStyle,
                    marginTop: 12,
                  }}
                >
                  View Backend Inventory
                </button>
              </div>
            ) : (
              <HoverNav
                onPrev={() =>
                  setPage((p) =>
                    Math.max(1, p - 1)
                  )
                }
                onNext={() =>
                  setPage((p) =>
                    Math.min(totalPages, p + 1)
                  )
                }
                disabledPrev={
                  page <= 1 || backendBusy
                }
                disabledNext={
                  page >= totalPages || backendBusy
                }
              >
                <DataTableCard
                  style={{
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 10,
                    boxShadow: "none",
                  }}
                  footer={
                    <PaginationBar
                      page={page}
                      totalPages={totalPages}
                      totalItems={
                        filteredData.length
                      }
                      itemLabel="records"
                      disabled={
                        refreshing || backendBusy
                      }
                      onPrevious={() =>
                        setPage((p) =>
                          Math.max(1, p - 1)
                        )
                      }
                      onNext={() =>
                        setPage((p) =>
                          Math.min(
                            totalPages,
                            p + 1
                          )
                        )
                      }
                    />
                  }
                >
                  {backendBusy &&
                  view === "backend" &&
                  !backendLoaded ? (
                    <div
                      style={{
                        padding: 48,
                        textAlign: "center",
                      }}
                    >
                      Loading backend inventory...
                    </div>
                  ) : paginatedData.length === 0 ? (
                    <div
                      style={{
                        padding: 48,
                        textAlign: "center",
                        color: PAGE.colors.muted,
                      }}
                    >
                      No inventory records found.
                    </div>
                  ) : (
                    <SheetDataTable
                      headers={activeHeaders}
                      rows={paginatedData}
                      renderHeader={
                        normalizeHeaderName
                      }
                      renderCell={renderCell}
                      minWidth={1000}
                      cellFontSize={12}
                    />
                  )}
                </DataTableCard>
              </HoverNav>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
